import { useFormController } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { ExtendedDosage, isDosageBreakAppendable, isDosageUnitEditable } from '~libs.shared.prescription';

import {
   useDisplayedPortionUnit,
   useExtendedDosageStagesFieldArray,
   useExtendedDosageSubmit,
   useExtendedDosageTemplateActions,
   useExtendedDosageValidation,
} from './ExtendedDosageForm.hooks';
import { ExtendedDosageFormNotifications } from './ExtendedDosageFormNotifications';
import {
   ExtendedDosageFormBean,
   ExtendedDosageFormContext,
   ExtendedDosageFormController,
} from './ExtendedDosageForm.types';
import { AddStageLinks } from './fields/AddStageLinks';
import { PatientInstructionField } from './fields/PatientInstructionField';
import { PortionUnitSelect } from './fields/PortionUnitSelect';
import { StageFieldList } from './fields/StageFieldList';
import { TemplateNameField } from './fields/TemplateNameField';

export type ExtendedDosageFormRenderProps = Pick<
   ExtendedDosageFormController,
   'handleSubmit' | 'formState' | 'watch' | 'control'
> & {
   saveBlocked: boolean;
   stagesEmpty: boolean;
   getTemplateExtendedDosage: () => ExtendedDosage;
   applyTemplateExtendedDosage: (extendedDosage: ExtendedDosage) => void;
};

type ExtendedDosageFormProps = {
   readonly children: (renderProps: ExtendedDosageFormRenderProps) => JSX.Element;
   readonly initializer: () => ExtendedDosageFormBean;
   readonly portionUnit: string | null;
   readonly context: ExtendedDosageFormContext;
};

export const ExtendedDosageForm = (props: ExtendedDosageFormProps): JSX.Element => {
   const initialValues = props.initializer();
   const controller = useFormController<ExtendedDosageFormBean>(initialValues);
   const { register, watch, setValue, trigger, control, getValues, handleSubmit, formState } = controller;

   const portionUnit = watch('portionUnit') ?? props.portionUnit;
   const displayedPortionUnit = useDisplayedPortionUnit(props.context, portionUnit);

   const stagesFieldArray = useExtendedDosageStagesFieldArray(control, getValues);
   const validation = useExtendedDosageValidation({
      ...controller,
      stagesFieldArray: stagesFieldArray,
      portionUnit: portionUnit,
      context: props.context,
   });
   const handleSubmitStages = useExtendedDosageSubmit({
      handleSubmit: handleSubmit,
      getStages: stagesFieldArray.getStages,
      portionUnit: portionUnit,
      saveBlocked: validation.saveBlocked,
   });

   const templateActions = useExtendedDosageTemplateActions({
      ...controller,
      getStages: stagesFieldArray.getStages,
      portionUnit: portionUnit,
   });

   return (
      <StyledFlex direction={'column'} gap={'16px'} as={'form'} noValidate>
         {props.context.mode === 'TEMPLATE' ? <TemplateNameField register={register} /> : null}
         {props.context.mode === 'PRESCRIPTION' && isDosageUnitEditable(props.context.medicine) ? (
            <PortionUnitSelect register={register} defaultValue={portionUnit} medicine={props.context.medicine} />
         ) : null}
         <StyledFlex direction={'column'} gap={'8px'}>
            <StageFieldList
               register={register}
               watch={watch}
               setValue={setValue}
               trigger={trigger}
               fields={stagesFieldArray.fields}
               stageValidations={validation.stageValidations}
               portionUnit={displayedPortionUnit}
               onRemove={stagesFieldArray.remove}
               onDuplicate={stagesFieldArray.duplicate}
               onChangeStageMode={stagesFieldArray.changeStageMode}
            />
            <ExtendedDosageFormNotifications
               stagesEmpty={validation.stagesEmpty}
               breakMisplaced={validation.breakMisplaced}
               zeroDoseCycle={validation.zeroDoseCycleVisible}
               treatmentDaysCountExceeded={validation.treatmentDaysCountExceeded}
               exceededPackagesCount={validation.exceededPackagesCount}
            />
         </StyledFlex>
         <AddStageLinks
            onAddCycle={stagesFieldArray.appendFrequencyCycle}
            onAddBreak={stagesFieldArray.appendBreak}
            breakAppendable={isDosageBreakAppendable(validation.stageClasses)}
         />
         {props.context.mode === 'PRESCRIPTION' ? (
            <PatientInstructionField register={register} defaultValue={initialValues.patientInstruction ?? null} />
         ) : null}
         {props.children({
            handleSubmit: handleSubmitStages,
            formState: formState,
            watch: watch,
            control: control,
            saveBlocked: validation.saveBlocked,
            stagesEmpty: validation.stagesEmpty,
            ...templateActions,
         })}
      </StyledFlex>
   );
};
